import React, { useCallback, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server.browser';

import { AppContext } from '@r-app/app-context';

import { reconcileOverride, type OverrideMergeStrategy } from './reconcile-override';
import { sanitizeOverrideHtml } from './sanitize-override-html';

export type EditableTextProps = {
	overrideKey: string;
	/** Older key for the same field, read-only. Lets edits saved under a superseded key still render; new edits always write overrideKey. */
	legacyOverrideKey?: string;
	/** Pre-extracted value from a superseded field this one was split out of (e.g. one line pulled out of a combined block) — used when neither overrideKey nor legacyOverrideKey has a stored value. Unlike legacyOverrideKey this isn't a map lookup, so the caller resolves it. */
	legacyOverrideValue?: string;
	/** The base captured for legacyOverrideValue's original field, if one exists — lets a real edit made before the split still be honored when the model hasn't moved since. */
	legacyOverrideBase?: string;
	/** The map key legacyOverrideValue was read from — lets shift+right-click clear it even though it isn't looked up by key directly. */
	legacyClearKey?: string;
	/** How automation and the manual edit are merged. 'keyed' aligns list rows by SKU token (the appliance list); 'prose' (default) runs a three-way line merge; 'derived' reclaims a fully computed field once the model moves past its captured base. */
	mergeStrategy?: OverrideMergeStrategy;
	children?: React.ReactNode;
	element?: 'div' | 'span' | 'td' | 'th';
	style?: React.CSSProperties;
	className?: string;
};

/** The field's generated content as an HTML string — the merge base captured on edit and the automation input reconciled on render. */
const generatedHtmlOf = (children: React.ReactNode): string => sanitizeOverrideHtml(renderToStaticMarkup(<>{children}</>));

/** Which key actually holds this field's value — the current one, else the superseded one. */
const resolveStoredKey = (overrides: Record<string, string> | undefined, overrideKey: string, legacyOverrideKey?: string): string | undefined => {
	if (overrides?.[overrideKey] !== undefined) return overrideKey;
	if (legacyOverrideKey !== undefined && overrides?.[legacyOverrideKey] !== undefined) return legacyOverrideKey;
	return undefined;
};

/**
 * A contentEditable wrapper that persists text edits directly to the kitchen's
 * blueprintOverrides map on every input event. Stores innerHTML, not innerText: the generated
 * content carries bold labels and red disclosures ("Customer Supplied", "Countertop Must Be
 * Templated Onsite"), and flattening to text silently destroyed them on the first edit.
 * Legacy plain-text overrides still render correctly — `white-space: pre-wrap` keeps their
 * newlines, and entities decode through the same HTML path.
 */
export const EditableText = (props: EditableTextProps): React.ReactElement => {
	const { overrideKey, legacyOverrideKey, legacyOverrideValue, legacyOverrideBase, legacyClearKey, mergeStrategy = 'prose', children, element: Element = 'div', style, className } = props;
	const context = useContext(AppContext);
	const kitchen = context?.controller?.kitchen ?? null;

	const overrides = kitchen?.blueprintOverrides;
	const storedKey = resolveStoredKey(overrides, overrideKey, legacyOverrideKey);
	const override = storedKey !== undefined ? overrides?.[storedKey] : legacyOverrideValue;

	// Re-base on every edit: the value the user is now editing was reconciled against the current
	// generated content, so that same content becomes the base the next model change merges from.
	const handleInput = useCallback((e: React.FormEvent<HTMLElement>): void => {
		const html = (e.currentTarget as HTMLElement).innerHTML ?? '';
		kitchen?.setBlueprintOverride(overrideKey, sanitizeOverrideHtml(html), generatedHtmlOf(children));
	}, [kitchen, overrideKey, children]);

	// Shift+right-click reverts to generated content — the only way back for a block whose styling
	// was already flattened by the old innerText storage (clearBlueprintOverride had no caller).
	// Shift-gated so an ordinary right-click can never silently discard someone's edit.
	const handleContextMenu = useCallback((e: React.MouseEvent<HTMLElement>): void => {
		const clearKey = storedKey ?? legacyClearKey;
		if (!e.shiftKey || clearKey === undefined) return;
		e.preventDefault();
		kitchen?.clearBlueprintOverride(clearKey);
	}, [kitchen, storedKey, legacyClearKey]);

	// Tab indents at the caret (PowerPoint behavior) instead of moving focus to the next element.
	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>): void => {
		if (e.key !== 'Tab') return;
		e.preventDefault();
		document.execCommand('insertText', false, '\t');
	}, []);

	const elementProps = {
		contentEditable: true,
		suppressContentEditableWarning: true,
		onInput: handleInput,
		onKeyDown: handleKeyDown,
		onContextMenu: handleContextMenu,
		title: override !== undefined ? 'Edited — Shift+right-click to reset to generated content' : undefined,
		style: { whiteSpace: 'pre-wrap' as const, tabSize: 4, ...style },
		className
	};

	// Distinct keys swap the whole element on a generated<->override switch, so React never reconciles the browser-mutated contentEditable interior — a stale reconcile there throws removeChild NotFoundError.
	// Merged on read so automation (appliance changes, regenerated notes) flows through the manual edit instead of the frozen override winning forever; sanitised so anything already persisted is scrubbed too.
	if (override !== undefined) {
		const merged = reconcileOverride({
			generated: generatedHtmlOf(children),
			manual: override,
			base: storedKey !== undefined ? kitchen?.getBlueprintOverrideBase(storedKey) : legacyOverrideBase,
			strategy: mergeStrategy
		});
		return <Element key='override' {...elementProps} dangerouslySetInnerHTML={{ __html: sanitizeOverrideHtml(merged) }} />;
	}

	return <Element key='generated' {...elementProps}>{children}</Element>;
};
